import NotificationService from '@/components/notification-service';
import { STATUS } from '@/constants';
import FrontendHttpClient from '@/utils/http-handler/frontend-http-client';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import useSWR from 'swr';
import { CHError } from '@/utils/errors';

import { route } from 'nextjs-routes';
import { useStore } from './use-store';
import { useNotes } from './use-notes';
import { usePageContext } from './use-page-context';
import { useCertificate } from './use-certificate';
import { useLobTemplates } from './use-lob-templates';

export const useTemplate = () => {
  const [pageContext, setPageContext] = usePageContext();
  const [{ user, unsavedNotesExist }] = useStore();

  const notes = useNotes();
  const router = useRouter();
  const certificate = useCertificate();
  const lobTemplates = useLobTemplates({ enableAllForms: true });

  const { saveTemplateLoading, template, certContext } = pageContext;
  const { templateMode, userTemplateId } = router.query;

  const isRenewal = router.pathname === '/renew/edit-cert-template';

  const apiRoute = isRenewal
    ? '/api/v2/renewal_cert_templates'
    : '/api/v2/certificate_templates';

  const { data, mutate } = useSWR(
    userTemplateId ? [route({ pathname: apiRoute }), userTemplateId] : null,
    async ([url, templateId]) => {
      const res = await FrontendHttpClient.get(url, { id: templateId });
      return res.data;
    }
  );

  const save = useCallback(async () => {
    const { id, status, templateName } = template;

    setPageContext({
      isEditorReady: false,
      saveTemplateLoading: true,
    });

    try {
      const templateData = {
        ...certContext.save(),
        status: template.status === STATUS.draft ? STATUS.active : status,
        updatePDF: true,
        action: 'edit',
      };

      if (!templateData.lobTemplates.length) {
        throw new CHError('A template must include at least one policy');
      }

      if (unsavedNotesExist) {
        await notes.save();
      }

      await FrontendHttpClient.put(
        route({
          pathname: `${apiRoute}/[id]`,
          query: { id },
        }),
        templateData
      );

      certificate.mutateCertContext();

      NotificationService.success(`Template ${templateName} saved`);
    } catch (err) {
      setPageContext({
        isEditorReady: true,
        saveTemplateLoading: false,
      });

      let errorMessage;
      switch (true) {
        case err.response?.status === 409:
          errorMessage = `Template by the name "${templateName}" already exist`;
          break;
        case err instanceof CHError:
          errorMessage = err.message;
          break;
        default:
          errorMessage = 'Error saving template';
      }

      NotificationService.error(errorMessage);

      throw err;
    }
  }, [
    certContext,
    certificate,
    notes,
    setPageContext,
    template,
    unsavedNotesExist,
    apiRoute,
  ]);

  const create = useCallback(
    async name => {
      setPageContext({
        isEditorReady: false,
        saveTemplateLoading: true,
      });

      try {
        const templateData = {
          templateName: name,
          status: STATUS.active,
          ...certContext.save(),
          createdByUserId: user.id,
          action: 'create',
        };

        if (!templateData.lobTemplates.length) {
          throw new CHError('A template must include at least one policy');
        }

        if (unsavedNotesExist) {
          await notes.save();
        }

        await FrontendHttpClient.post(apiRoute, {
          template: templateData,
        });

        NotificationService.success(`Template ${name} saved`);
      } catch (err) {
        if (err.response?.status === 409) {
          NotificationService.error(
            `Template by the name "${name}" already exist`
          );
        } else {
          NotificationService.error(
            err instanceof CHError ? err.message : 'Error creating template'
          );
        }
        setPageContext({
          isEditorReady: true,
          saveTemplateLoading: false,
        });
        throw err;
      }
      setPageContext({
        isEditorReady: true,
        saveTemplateLoading: false,
      });
    },
    [certContext, notes, setPageContext, unsavedNotesExist, user.id, apiRoute]
  );

  const remove = useCallback(async () => {
    const res = await FrontendHttpClient.post(
      route({
        pathname: `${apiRoute}/[id]/[...params]`,
        query: { id: template.id, params: ['delete'] },
      })
    );

    return res.data;
  }, [template, apiRoute]);

  const rename = useCallback(
    async newName => {
      try {
        const res = await FrontendHttpClient.post(
          route({
            pathname: `${apiRoute}/[id]/[...params]`,
            query: { id: template.id, params: ['rename'] },
          }),
          { templateName: newName, insuredId: template.insuredId }
        );
        mutate();
        return res.data;
      } catch (err) {
        if (err.response?.status === 409) {
          NotificationService.error(
            `Template by the name "${newName}" already exist`
          );
        }
        return { success: false };
      }
    },
    [mutate, template, apiRoute]
  );

  const activate = useCallback(async () => {
    const res = await FrontendHttpClient.post(
      route({
        pathname: `${apiRoute}/[id]/[...params]`,
        query: { id: template.id, params: ['activate'] },
      })
    );

    mutate();
    return res.data;
  }, [mutate, template, apiRoute]);

  const deactivate = useCallback(async () => {
    const res = await FrontendHttpClient.post(
      route({
        pathname: `${apiRoute}/[id]/[...params]`,
        query: { id: template.id, params: ['deactivate'] },
      })
    );

    mutate();
    return res.data;
  }, [mutate, template, apiRoute]);

  useEffect(() => {
    if (data) {
      setPageContext({ template: data.record[0] });
    }
  }, [data, setPageContext]);

  return {
    mode: templateMode,
    isLoading: saveTemplateLoading,
    data: template,
    get: mutate,
    save,
    create,
    remove,
    rename,
    activate,
    deactivate,
  };
};
